import React, { useState, useEffect } from 'react';
import { 
  LineChart, Line, AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  ComposedChart, ReferenceLine
} from 'recharts';
import { TrendingUp, Package, AlertTriangle, Calendar, Filter } from 'lucide-react';
import { colors } from '../../../constants/theme';
import { Card } from '../../ui/Card';
import { Button } from '../../ui/Button';
import { Badge } from '../../ui/Badge';
import { Select } from '../../ui/Select';

interface StockTrendData {
  date: string;
  stockLevel: number;
  inbound: number;
  outbound: number;
  optimalStock: number;
  minStock: number;
  maxStock: number;
  forecast: number;
}

interface CategoryTrend {
  category: string;
  data: StockTrendData[];
  averageStock: number;
  turnoverTrend: 'improving' | 'stable' | 'declining';
}

const StockTrendChart: React.FC = () => {
  const [trendData, setTrendData] = useState<StockTrendData[]>([]);
  const [selectedPeriod, setSelectedPeriod] = useState<'7d' | '30d' | '90d'>('30d');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [chartType, setChartType] = useState<'area' | 'line' | 'composed'>('composed');
  const [showForecast, setShowForecast] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  
  // 在庫推移データを生成
  const generateTrendData = () => {
    const days = selectedPeriod === '7d' ? 7 : selectedPeriod === '30d' ? 30 : 90;
    const data: StockTrendData[] = [];
    const baseStock = 5000;
    const optimal = 4500;
    
    for (let i = days; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      
      // 在庫変動をシミュレート
      const dailyDemand = 200 + Math.sin(i * 0.5) * 50 + Math.random() * 30;
      const inbound = i % 7 === 0 ? 800 + Math.random() * 200 : 0; // 週次入荷
      const stockLevel = baseStock + Math.sin(i * 0.3) * 500 + Math.random() * 200;
      
      data.push({
        date: date.toLocaleDateString('ja-JP', { month: 'short', day: 'numeric' }),
        stockLevel: Math.round(stockLevel),
        inbound: Math.round(inbound),
        outbound: Math.round(dailyDemand),
        optimalStock: optimal,
        minStock: optimal * 0.6,
        maxStock: optimal * 1.4,
        forecast: i > days - 7 ? 0 : Math.round(stockLevel * (1 + Math.random() * 0.1))
      });
    }
    
    // 将来予測を追加
    if (showForecast) {
      for (let i = 1; i <= 7; i++) {
        const date = new Date();
        date.setDate(date.getDate() + i);
        const forecastStock = data[data.length - 1].stockLevel - (200 * i) + (i === 7 ? 800 : 0);
        
        data.push({
          date: date.toLocaleDateString('ja-JP', { month: 'short', day: 'numeric' }),
          stockLevel: 0,
          inbound: i === 7 ? 800 : 0,
          outbound: 200,
          optimalStock: optimal,
          minStock: optimal * 0.6,
          maxStock: optimal * 1.4,
          forecast: Math.max(optimal * 0.3, Math.round(forecastStock))
        });
      }
    }
    
    setTrendData(data);
  };
  
  useEffect(() => {
    setIsLoading(true);
    setTimeout(() => {
      generateTrendData();
      setIsLoading(false);
    }, 500);
  }, [selectedPeriod, selectedCategory, showForecast]);
  
  const runTrendAnalysis = () => {
    setIsLoading(true);
    setTimeout(() => {
      generateTrendData();
      setIsLoading(false);
      
      // 分析結果を計算
      const avgStock = trendData.reduce((sum, d) => sum + (d.stockLevel || 0), 0) / trendData.filter(d => d.stockLevel > 0).length;
      const optimalStock = trendData[0]?.optimalStock || 4500;
      const efficiency = ((optimalStock / avgStock) * 100).toFixed(1);
      const riskDays = trendData.filter(d => d.stockLevel > 0 && d.stockLevel < d.minStock).length;
      
      alert(`✅ 在庫推移分析を実行しました！\n\n分析結果:\n- 平均在庫: ${Math.round(avgStock).toLocaleString()}個\n- 在庫効率: ${efficiency}%\n- 適正在庫: ${optimalStock.toLocaleString()}個\n- 欠品リスク日数: ${riskDays}日\n\n最適化提案:\n${efficiency < 90 ? '在庫が過剰傾向です。発注量を削減してください。' : '在庫レベルは適正範囲内です。'}`);
    }, 1500);
  };
  
  // カテゴリー別サマリー
  const categoryStats = [
    { name: '飲料', avgStock: 8500, trend: 'stable', efficiency: 92 },
    { name: '菓子', avgStock: 6200, trend: 'improving', efficiency: 88 },
    { name: '日配品', avgStock: 4300, trend: 'declining', efficiency: 95 },
    { name: '冷凍食品', avgStock: 3800, trend: 'stable', efficiency: 90 },
    { name: '生鮮', avgStock: 2100, trend: 'improving', efficiency: 97 }
  ];
  
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div 
          className="bg-white p-3 rounded-lg shadow-lg border"
          style={{ borderColor: colors.ui.border }}
        >
          <p className="font-medium mb-2" style={{ color: colors.text.primary }}>
            {label}
          </p>
          {payload.map((entry: any, index: number) => (
            <div key={index} className="flex items-center justify-between gap-3 text-sm">
              <span style={{ color: entry.color }}>{entry.name}:</span>
              <span style={{ color: colors.text.primary }}>
                {entry.value.toLocaleString()}
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };
  
  return (
    <div className="space-y-6">
      {/* ヘッダー */}
      <Card className="p-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold mb-1" style={{ color: colors.text.primary }}>
              在庫推移分析
            </h3>
            <p className="text-sm" style={{ color: colors.text.secondary }}>
              在庫レベルの推移と将来予測
            </p>
          </div>
          <div className="flex gap-2">
            <Select
              value={selectedPeriod}
              onChange={(value) => setSelectedPeriod(value as any)}
              options={[
                { value: '7d', label: '過去7日' },
                { value: '30d', label: '過去30日' },
                { value: '90d', label: '過去90日' }
              ]}
            />
            <Select
              value={chartType}
              onChange={(value) => setChartType(value as any)}
              options={[
                { value: 'composed', label: '複合チャート' },
                { value: 'area', label: 'エリアチャート' },
                { value: 'line', label: 'ラインチャート' }
              ]}
            />
            <Button 
              variant="primary"
              onClick={runTrendAnalysis}
              disabled={isLoading}
            >
              <TrendingUp size={16} className={isLoading ? 'animate-pulse' : ''} />
              {isLoading ? '分析中...' : '推移分析実行'}
            </Button>
          </div>
        </div>
        
        {/* フィルター */}
        <div className="flex items-center gap-4 mb-4">
          <div className="flex items-center gap-2">
            <span className="text-sm" style={{ color: colors.text.secondary }}>カテゴリー:</span>
            <Select
              value={selectedCategory}
              onChange={(value) => setSelectedCategory(value as string)}
              options={[
                { value: 'all', label: '全体' },
                { value: 'beverage', label: '飲料' },
                { value: 'snack', label: '菓子' },
                { value: 'daily', label: '日配品' },
                { value: 'frozen', label: '冷凍食品' },
                { value: 'fresh', label: '生鮮' }
              ]}
            />
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={showForecast}
              onChange={(e) => setShowForecast(e.target.checked)}
              className="cursor-pointer"
            />
            <span className="text-sm" style={{ color: colors.text.secondary }}>
              将来予測を表示
            </span>
          </div>
        </div>
      </Card>
      
      {/* チャート */}
      <Card className="p-4">
        <div className="mb-4">
          <div className="flex items-center gap-4 mb-2">
            <h4 className="font-medium" style={{ color: colors.text.primary }}>
              在庫レベル推移
            </h4>
            <div className="flex gap-3">
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: colors.accent.blue }}></div>
                <span className="text-xs" style={{ color: colors.text.secondary }}>実在庫</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: colors.accent.green }}></div>
                <span className="text-xs" style={{ color: colors.text.secondary }}>適正在庫</span>
              </div>
              {showForecast && (
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded-full border-2" style={{ borderColor: colors.accent.purple }}></div>
                  <span className="text-xs" style={{ color: colors.text.secondary }}>予測</span>
                </div>
              )}
            </div>
          </div>
        </div>
        
        {isLoading ? (
          <div className="h-80 flex items-center justify-center">
            <div className="animate-pulse text-center">
              <Package size={48} style={{ color: colors.text.secondary }} className="mx-auto mb-2" />
              <p style={{ color: colors.text.secondary }}>データを読み込み中...</p>
            </div>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={320}>
            {chartType === 'composed' ? (
              <ComposedChart data={trendData}>
                <CartesianGrid strokeDasharray="3 3" stroke={colors.ui.border} />
                <XAxis 
                  dataKey="date" 
                  tick={{ fontSize: 12, fill: colors.text.secondary }}
                />
                <YAxis 
                  tick={{ fontSize: 12, fill: colors.text.secondary }}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                
                {/* 在庫範囲 */}
                <Area
                  type="monotone"
                  dataKey="maxStock"
                  stackId="1"
                  stroke="none"
                  fill={colors.ui.bgSecondary}
                  name="最大在庫"
                  opacity={0.3}
                />
                <Area
                  type="monotone"
                  dataKey="minStock"
                  stackId="2"
                  stroke="none"
                  fill={colors.ui.bg}
                  name="最小在庫"
                  opacity={0.3}
                />
                
                {/* 実在庫 */}
                <Line
                  type="monotone"
                  dataKey="stockLevel"
                  stroke={colors.accent.blue}
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  name="実在庫"
                />
                
                {/* 適正在庫ライン */}
                <Line
                  type="monotone"
                  dataKey="optimalStock"
                  stroke={colors.accent.green}
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  dot={false}
                  name="適正在庫"
                />
                
                {/* 予測 */}
                {showForecast && (
                  <Line
                    type="monotone"
                    dataKey="forecast"
                    stroke={colors.accent.purple}
                    strokeWidth={2}
                    strokeDasharray="3 3"
                    dot={{ r: 3 }}
                    name="予測"
                  />
                )}
                
                {/* 入出庫 */}
                <Bar dataKey="inbound" fill={colors.accent.cyan} name="入庫" opacity={0.6} />
                <Bar dataKey="outbound" fill={colors.accent.orange} name="出庫" opacity={0.6} />
              </ComposedChart>
            ) : chartType === 'area' ? (
              <AreaChart data={trendData}>
                <CartesianGrid strokeDasharray="3 3" stroke={colors.ui.border} />
                <XAxis dataKey="date" tick={{ fontSize: 12, fill: colors.text.secondary }} />
                <YAxis tick={{ fontSize: 12, fill: colors.text.secondary }} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Area type="monotone" dataKey="stockLevel" stackId="1" stroke={colors.accent.blue} fill={colors.accent.blue} opacity={0.6} name="実在庫" />
                <Area type="monotone" dataKey="optimalStock" stackId="2" stroke={colors.accent.green} fill={colors.accent.green} opacity={0.3} name="適正在庫" />
              </AreaChart>
            ) : (
              <LineChart data={trendData}>
                <CartesianGrid strokeDasharray="3 3" stroke={colors.ui.border} />
                <XAxis dataKey="date" tick={{ fontSize: 12, fill: colors.text.secondary }} />
                <YAxis tick={{ fontSize: 12, fill: colors.text.secondary }} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Line type="monotone" dataKey="stockLevel" stroke={colors.accent.blue} strokeWidth={2} dot={{ r: 3 }} name="実在庫" />
                <Line type="monotone" dataKey="optimalStock" stroke={colors.accent.green} strokeWidth={2} strokeDasharray="5 5" dot={false} name="適正在庫" />
                {showForecast && (
                  <Line type="monotone" dataKey="forecast" stroke={colors.accent.purple} strokeWidth={2} strokeDasharray="3 3" dot={{ r: 3 }} name="予測" />
                )}
              </LineChart>
            )}
          </ResponsiveContainer>
        )}
      </Card>
      
      {/* カテゴリー別サマリー */}
      <Card className="p-4">
        <h4 className="font-medium mb-4" style={{ color: colors.text.primary }}>
          カテゴリー別在庫効率
        </h4>
        <div className="grid grid-cols-5 gap-4">
          {categoryStats.map((cat) => (
            <div 
              key={cat.name}
              className="p-3 rounded-lg border"
              style={{ borderColor: colors.ui.border }}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium" style={{ color: colors.text.primary }}>
                  {cat.name}
                </span>
                <Badge 
                  variant={cat.trend === 'improving' ? 'success' : cat.trend === 'declining' ? 'error' : 'secondary'}
                  size="sm"
                >
                  {cat.trend === 'improving' ? '改善' : cat.trend === 'declining' ? '悪化' : '安定'}
                </Badge>
              </div>
              <div className="space-y-1">
                <div className="flex justify-between text-xs">
                  <span style={{ color: colors.text.secondary }}>平均在庫</span>
                  <span style={{ color: colors.text.primary }}>{cat.avgStock.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span style={{ color: colors.text.secondary }}>効率性</span>
                  <span style={{ color: colors.text.primary }}>{cat.efficiency}%</span>
                </div>
              </div>
              <div className="mt-2 h-1 bg-gray-100 rounded-full overflow-hidden">
                <div 
                  className="h-full rounded-full transition-all"
                  style={{ 
                    width: `${cat.efficiency}%`,
                    backgroundColor: cat.efficiency > 90 ? colors.accent.green : cat.efficiency > 80 ? colors.accent.blue : colors.accent.orange
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};

export default StockTrendChart;
